/**
 * ImpersonationBanner — shown in the Control Center when a Super Admin
 * is viewing the client's dashboard via the ?impersonate= URL param.
 *
 * Amber banner at the top of the page with the impersonated client's ID
 * and a link back to Mission Control.
 */

import { clearImpersonation } from '@bas/shared'

interface ImpersonationBannerProps {
  clientId: string
  businessName?: string
  /**
   * REVIEW mode (the default since 2026-09-22, CC review #24): every write is refused
   * server-side — Firestore rules and the /api proxy — so it is safe to click
   * anything. ACT mode writes for real, and the banner turns red to say so.
   */
  reviewMode?: boolean
}

export function ImpersonationBanner({ clientId, businessName, reviewMode }: ImpersonationBannerProps) {
  const who = businessName || clientId
  return (
    <div className={`${reviewMode ? 'bg-teal-700' : 'bg-red-600'} text-white px-4 py-2.5 flex items-center justify-between flex-shrink-0`}>
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
        <span className="text-sm font-semibold">
          {reviewMode ? (
            <>Reviewing <span className="font-bold">{who}</span> — read-only. Click anything; nothing you do changes their account.</>
          ) : (
            <>Acting as <span className="font-bold">{who}</span> — full access. Anything you click changes their account.</>
          )}
        </span>
      </div>
      <a
        href="/admin/clients"
        onClick={() => clearImpersonation()}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/20 hover:bg-black/30 text-white text-xs font-semibold transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Mission Control
      </a>
    </div>
  )
}
